import { apolloClient } from '@/plugins/apollo';
import { ExportDownloadDto, Reference } from '@/models';
import {
  ExportReferencesToRis,
  UpdateReferenceFields,
  ResolveReferenceDuplicates,
  UnmarkReferenceAsDuplicate,
  UpdateReferenceTags,
  GetReferencesByTagId,
  DuplicateSuggestions,
  SearchReferences,
} from '@/graphql';

const removeReferencesFromDuplicateGroupQueryCache = (
  cache,
  fieldName,
  forReferenceId,
  referenceIdsToRemove
) => {
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      [fieldName](existingConnection, { readField }) {
        return filterOutReferencesFromDuplicateGroupQueryCache(
          forReferenceId,
          referenceIdsToRemove,
          existingConnection,
          readField
        );
      },
    },
  });
};

const filterOutReferencesFromDuplicateGroupQueryCache = (
  forReferenceId,
  referenceIdsToRemove,
  existingConnection,
  readField
) => {
  if (!referenceIdsToRemove || referenceIdsToRemove.length === 0)
    return existingConnection;

  return {
    ...existingConnection,
    edges: existingConnection.edges
      .map((edge) => {
        const mainCopyRef = edge.node.mainCopy;
        const duplicateRefs = edge.node.duplicates;
        const mainCopyRefId = readField('id', mainCopyRef);
        if (mainCopyRefId !== forReferenceId) return edge;

        const newDuplicates = duplicateRefs.filter((ref) => {
          const refId = readField('id', ref);
          return !referenceIdsToRemove.includes(refId);
        });
        if (newDuplicates.length === 0) {
          return null;
        }
        return {
          ...edge,
          node: {
            ...edge.node,
            mainCopy: mainCopyRef,
            duplicates: newDuplicates,
          },
        };
      })
      .filter(Boolean),
  };
};

const actions = {
  async export(context, { reviewId, outcomes }) {
    const response = await apolloClient.query({
      query: ExportReferencesToRis,
      variables: {
        reviewId,
        outcomes,
      },
      fetchPolicy: 'no-cache',
    });
    const data = response.data.exportReferencesToRis;
    return new ExportDownloadDto(data);
  },
  async updateReferenceFields(context, reference) {
    const response = await apolloClient.mutate({
      mutation: UpdateReferenceFields,
      variables: {
        reference: reference.id,
        publication: reference.publication.withoutTypename(),
      },
    });
    const data = response.data.updateReferenceFields;
    return new Reference(data);
  },
  async resolveDuplicates(
    context,
    { forReferenceId, keepReferenceIds = [], removeReferenceIds = [] }
  ) {
    const refetchQueries = [
      'GetReviewForReviewPage',
      'GetReferenceDuplicateGroupsReviewNeeded',
    ];
    if (keepReferenceIds.length > 0) {
      refetchQueries.push(
        'GetReferenceDuplicateGroupsResolvedAsNotDuplicates'
      );
    }
    if (removeReferenceIds.length > 0) {
      refetchQueries.push(
        ...[
          'GetReferenceDuplicateGroups',
          'GetReferenceDuplicateGroupsAutoResolved',
          'GetReferenceDuplicateGroupsResolvedByUser',
          // A newly-marked duplicate must drop out of whichever screening/list view is open.
          'GetReferencesPendingScreening',
          'GetReferencesScreened',
          'GetReferencesAwaitingYouScreening',
          'GetReferencesForScreeningConflictReview',
          'GetScreeningCountsForReview',
          'GetReviewScreeningInsights',
          'SearchReferences',
          'GetReferencesByFilters',
        ]
      );
    }
    const response = await apolloClient.mutate({
      mutation: ResolveReferenceDuplicates,
      variables: {
        forReferenceId,
        keepReferenceIds,
        removeReferenceIds,
      },
      update: (cache) => {
        removeReferencesFromDuplicateGroupQueryCache(
          cache,
          'referenceDuplicateGroups',
          forReferenceId,
          keepReferenceIds
        );
        removeReferencesFromDuplicateGroupQueryCache(
          cache,
          'referenceDuplicateGroupsAutoResolved',
          forReferenceId,
          keepReferenceIds
        );
        removeReferencesFromDuplicateGroupQueryCache(
          cache,
          'referenceDuplicateGroupsResolvedByUser',
          forReferenceId,
          keepReferenceIds
        );
        removeReferencesFromDuplicateGroupQueryCache(
          cache,
          'referenceDuplicateGroupsResolvedAsNotDuplicates',
          forReferenceId,
          removeReferenceIds
        );
        removeReferencesFromDuplicateGroupQueryCache(
          cache,
          'referenceDuplicateGroupsReviewNeeded',
          forReferenceId,
          [...keepReferenceIds, ...removeReferenceIds]
        );
      },
      refetchQueries,
    });
    return response.data.resolveReferenceDuplicates;
  },
  /**
   * Reverses a manual "mark as duplicate". Unlike marking something "not a
   * duplicate", this records no detection veto — it restores the state from
   * before the mark, so it is safe to offer as an Undo.
   */
  async unmarkAsDuplicate(context, { referenceId }) {
    return await apolloClient.mutate({
      mutation: UnmarkReferenceAsDuplicate,
      variables: { referenceId },
      refetchQueries: [
        'GetReviewForReviewPage',
        'GetReferenceDuplicateGroups',
        'GetReferenceDuplicateGroupsAutoResolved',
        'GetReferenceDuplicateGroupsResolvedByUser',
        'GetReferenceDuplicateGroupsReviewNeeded',
        // The restored reference must reappear in whichever list view is open.
        'GetReferencesPendingScreening',
        'GetReferencesScreened',
        'GetReferencesAwaitingYouScreening',
        'GetReferencesForScreeningConflictReview',
        'GetScreeningCountsForReview',
        'GetReviewScreeningInsights',
        'SearchReferences',
        'GetReferencesByFilters',
      ],
    });
  },
  async setReferenceTags(context, { reference, tags }) {
    const referenceId = reference.id;
    const tagIds = tags.map((tag) => tag.id);
    const response = await apolloClient.mutate({
      mutation: UpdateReferenceTags,
      variables: {
        referenceId,
        tagIds,
      },
    });
    return response.data.setTags;
  },
  async getReferencesUsingTag(context, tagId) {
    const response = await apolloClient.query({
      query: GetReferencesByTagId,
      variables: {
        tagId,
      },
      fetchPolicy: 'no-cache',
    });
    const results = response.data.referencesUsingTag;
    return results.map((data) => new Reference(data));
  },
  async getDuplicateSuggestions(
    context,
    { referenceId, limit = 10 }
  ) {
    const response = await apolloClient.query({
      query: DuplicateSuggestions,
      variables: { referenceId, limit },
      fetchPolicy: 'no-cache',
    });
    return (response.data.duplicateSuggestionsForReference || []).map(
      (s) => ({
        reference: new Reference(s.reference),
        confidence: s.confidence,
        reason: s.reason,
      })
    );
  },
  async searchReferencesInReview(
    context,
    { reviewId, searchInput, limit = 20 }
  ) {
    const response = await apolloClient.query({
      query: SearchReferences,
      variables: {
        searchInput: { review: reviewId, searchInput },
        offset: 0,
        limit,
      },
      fetchPolicy: 'no-cache',
    });
    return (response.data.searchReferences?.nodes || []).map(
      (node) => new Reference(node)
    );
  },
};

const state = {};

export default {
  namespaced: true,
  actions,
  state,
};
